import axios from 'axios';
// removed time formatting; frontend will use `createdAt` directly
import i18n from '../i18n/i18n';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

export interface Notification {
  id: number;
  title: string;
  message: string;
  read: boolean;
  type?: 'info' | 'success' | 'warning' | 'error';
  createdAt: string;
  link?: string;
  payload?: any;
}

interface ApiNotification {
  id: number;
  payload?: any;
  read: boolean;
  type: string;
  createdAt: string;
  link?: string;
}

// `createdAt` (epoch ms) is provided by the API; UI components should format it.

// Transform API response to frontend format
const translateFromKey = (key?: string, params?: any, fallback?: string) => {
  if (!key) return fallback || '';
  // Prefer message key for message translation, then title, then generic
  const titleKey = `notifications.${key}.title`;
  const msgKey = `notifications.${key}.message`;
  const tMsg = i18n.t(msgKey, params);
  if (tMsg !== msgKey) return tMsg;
  const tTitle = i18n.t(titleKey, params);
  if (tTitle !== titleKey) return tTitle;
  const tGeneric = i18n.t(`notifications.${key}`, params);
  if (tGeneric !== `notifications.${key}`) return tGeneric;
  return fallback || '';
};

const transformNotification = (n: ApiNotification): Notification => {
  const key = n.payload?.key;
  const params = n.payload?.params;
  // Expect payload to provide key + params; translate into title/message
  const translatedMessage = key ? String(translateFromKey(key, params, '')) : '';
  const titleKey = key ? `notifications.${key}.title` : undefined;
  const rawTitle = titleKey ? i18n.t(titleKey, params) : '';
  const translatedTitle = rawTitle !== titleKey ? String(rawTitle) : '';

  return {
    id: n.id,
    title: translatedTitle || '',
    message: translatedMessage || '',
    read: n.read,
    type: n.type as Notification['type'],
    createdAt: n.createdAt,
    link: n.link,
    payload: n.payload,
  };
};

// Store listeners for subscription pattern
let listeners: Array<(notifications: Notification[]) => void> = [];
let cachedNotifications: Notification[] = [];
let evtSource: EventSource | null = null;
let handshakeIntervalId: number | null = null;
let authStateEnabled = false;
const HANDSHAKE_INTERVAL_MS = 150_000; // renew every 2.5min (cookie TTL is 5min)

const canConnectStream = (): boolean => authStateEnabled && !!localStorage.getItem('token');

const startEventSource = () => {
  if (evtSource || !canConnectStream()) return;
  const token = localStorage.getItem('token');

  if (!token) return;

  // Handshake: request server to set an HttpOnly cookie for SSE authentication
  const doHandshake = async () => {
    try {
      if (!token) return;
      await fetch(`${API_URL}/notifications/stream/connect`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
    } catch (err) {
      // handshake may fail if not authenticated; SSE will error later
      // ignore
    }
  };

  doHandshake().finally(() => {
    if (!canConnectStream()) return;

    // Always connect to SSE endpoint without token in query string.
    // Authentication is performed via the HttpOnly `sse_token` cookie set by the handshake.
    const url = `${API_URL}/notifications/stream`;
    // eslint-disable-next-line no-undef
    evtSource = new EventSource(url, { withCredentials: true } as EventSourceInit);

    // start periodic handshake to renew HttpOnly cookie while stream is open
    startHandshakeInterval();

    // no-op onopen

    evtSource.onmessage = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as ApiNotification;

        // Refresh notifications from server to keep canonical list
        (async () => {
          try {
            const response = await axios.get<ApiNotification[]>(`${API_URL}/notifications`);
            cachedNotifications = response.data.map(transformNotification);
          } catch (err) {
            // fallback: insert event at top
            const n = transformNotification(data);
            cachedNotifications = [n, ...cachedNotifications].slice(0, 50);
          }
          notifyListeners();
        })();
      } catch (err) {
        // ignore parse errors
      }
    };

    evtSource.onerror = (ev) => {
      // try reconnect: close and retry after delay
      try {
        evtSource?.close();
      } catch {}
      evtSource = null;
      stopHandshakeInterval();
      if (canConnectStream()) {
        setTimeout(() => startEventSource(), 5000);
      }
    };
  });
};

const stopEventSource = () => {
  if (evtSource) {
    try {
      evtSource.close();
    } catch {}
    evtSource = null;
  }
};

const startHandshakeInterval = () => {
  if (handshakeIntervalId) return;
  handshakeIntervalId = window.setInterval(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/notifications/stream/connect`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).catch(() => {});
  }, HANDSHAKE_INTERVAL_MS) as unknown as number;
};

const stopHandshakeInterval = () => {
  if (handshakeIntervalId) {
    clearInterval(handshakeIntervalId);
    handshakeIntervalId = null;
  }
};

const stopAll = () => {
  stopEventSource();
  stopHandshakeInterval();
  // clear listeners but keep cached notifications
  listeners = [];
};

const notifyListeners = () => {
  listeners.forEach(listener => listener([...cachedNotifications]));
};

export const notificationService = {
  /**
   * Get all notifications for the current user
   */
  async getAll(): Promise<Notification[]> {
    try {
      const response = await axios.get<ApiNotification[]>(`${API_URL}/notifications`);
      cachedNotifications = response.data.map(transformNotification);
      return cachedNotifications;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return [];
    }
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    try {
      const response = await axios.get<{ count: number }>(`${API_URL}/notifications/count`);
      return response.data.count;
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
      return 0;
    }
  },

  /**
   * Mark a notification as read
   */
  async markAsRead(id: number): Promise<void> {
    try {
      await axios.patch(`${API_URL}/notifications/${id}/read`);
      cachedNotifications = cachedNotifications.map(n =>
        n.id === id ? { ...n, read: true } : n
      );
      notifyListeners();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    try {
      await axios.post(`${API_URL}/notifications/mark-all-read`);
      cachedNotifications = cachedNotifications.map(n => ({ ...n, read: true }));
      notifyListeners();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  },

  /**
   * Delete a notification
   */
  async delete(id: number): Promise<void> {
    try {
      await axios.delete(`${API_URL}/notifications/${id}`);
      cachedNotifications = cachedNotifications.filter(n => n.id !== id);
      notifyListeners();
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  },

  /**
   * Clear all notifications
   */
  async clearAll(): Promise<void> {
    try {
      await axios.delete(`${API_URL}/notifications`);
      cachedNotifications = [];
      notifyListeners();
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  },

  /**
   * Subscribe to notification changes
   */
  subscribe(listener: (notifications: Notification[]) => void): () => void {
    listeners.push(listener);
    // start SSE when first listener subscribes
    if (listeners.length === 1) startEventSource();

    return () => {
      listeners = listeners.filter(l => l !== listener);
      // stop SSE when no listeners remain
      if (listeners.length === 0) stopEventSource();
    };
  },

  /**
   * Refresh notifications from server
   */
  async refresh(): Promise<void> {
    await this.getAll();
    notifyListeners();
  },
  /**
   * Stop SSE connection and handshake (used on logout)
   */
  stop(): void {
    authStateEnabled = false;
    stopAll();
  },

  /**
   * Enable/disable notification streaming based on authentication lifecycle.
   */
  setAuthState(isAuthenticated: boolean): void {
    authStateEnabled = isAuthenticated;

    if (!isAuthenticated) {
      stopAll();
      return;
    }

    if (listeners.length > 0) {
      startEventSource();
    }
  },
};

export default notificationService;
